import React, { useEffect, useState, useRef } from 'react';
import { LayoutGrid, Search, PlusCircle, Filter, Loader2, ChevronDown, Music, Users, Calendar, Trash2, Edit, Maximize, Headphones, User } from 'lucide-react';
import { format, addDays, startOfWeek, addWeeks, subWeeks, parseISO, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { generateTimeSlots, getSettings, heute } from '../../../lib/settings';
import { getRooms, getRoomTags, raumbelegungenApi, type RoomTag, type Room as ApiRoom } from '../../../lib/rooms';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import { Label } from '../../../components/ui/label';
import { useRoomBookings } from '../../../hooks/useRoomBookings';
import { useStandort } from '../../../components/layouts/use-standort';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// Typen für Räume (erweitert mit optionalen UI-Feldern)
type Room = ApiRoom & {
  // UI-spezifische Felder (optional, können aus anderen Quellen kommen)
  type?: 'practice' | 'classroom' | 'concert' | 'recording';
  equipment?: string[];
  location?: string;
  description?: string;
};

// Context menu types and interfaces
interface ContextMenuPosition {
  x: number;
  y: number;
}
interface ContextMenuState {
  isOpen: boolean;
  position: ContextMenuPosition;
  type: 'booking' | 'empty';
  data: any; // Booking or time slot data
  room?: Room;
  day?: Date;
  time?: string;
}

const RoomManagement = () => {
  const { selectedStandortId } = useStandort();
  const [isLoading, setIsLoading] = useState(true);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableTags, setAvailableTags] = useState<RoomTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [minSize, setMinSize] = useState<number | ''>('');
  const [maxSize, setMaxSize] = useState<number | ''>('');
  const [activeView, setActiveView] = useState('grid');
  const [selectedDate, setSelectedDate] = useState(() => heute());
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(heute(), {
    weekStartsOn: 1
  }));
  
  // Generate week days for the current week
  const weekDays = Array.from({
    length: 7
  }, (_, i) => addDays(currentWeek, i));
  
  // Nutze Custom Hook für Raumbelegungen (Tag- oder Wochenbereich)
  const roomIds = rooms.map(r => r.id);
  const rangeDays = activeView === 'day' ? [selectedDate] : weekDays;
  const { bookings, refetch: refetchBookings } = useRoomBookings(roomIds, rangeDays);
  // Freie Raumbuchung (T-0092): anlegen, ändern, löschen.
  const canBook = usePermission(P.raumplan.belegungsplan_bearbeiten);
  // „Löschen“ nur mit Recht — sonst lehnte erst der Server ab (T-0186).
  // Räume anlegen und pflegen liegt unter „Räume & Tags“ (T-0333).
  const darfAnlegen = usePermission(P.raumplan.raeume_anlegen);
  const darfBearbeiten = usePermission(P.raumplan.raeume_bearbeiten);
  const darfVerwalten = darfAnlegen || darfBearbeiten;
  const [buchung, setBuchung] = useState<{
    open: boolean;
    belegungId?: number;
    roomId: number;
    roomName: string;
    date: string;
    start: string;
    end: string;
    title: string;
  }>({ open: false, roomId: 0, roomName: '', date: '', start: '09:00', end: '10:00', title: '' });
  const [buchungSaving, setBuchungSaving] = useState(false);
  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    position: {
      x: 0,
      y: 0
    },
    type: 'empty',
    data: null
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Reference to the calendar container for positioning
  const calendarRef = useRef<HTMLDivElement>(null);
  // Zeitraster aus „Kalender & Zeiten“ (ADM-19.1): Beginn und Intervall bestimmen
  // die Position der Buchungen — vorher fest 8 Uhr und 15 Minuten.
  const timeSlots = generateTimeSlots();
  const rasterStartMinuten = (timeSlots[0]?.hour ?? 8) * 60 + (timeSlots[0]?.minute ?? 0);
  const rasterIntervall = (() => {
    const wert = Number(getSettings().timeInterval);
    return Number.isFinite(wert) && wert > 0 ? wert : 15;
  })();
  
  const fetchRoomsData = async () => {
    setIsLoading(true);
    try {
      const data = await getRooms(selectedStandortId || undefined, {
        tagIds: selectedTagIds,
        minSize: typeof minSize === 'number' ? minSize : undefined,
        maxSize: typeof maxSize === 'number' ? maxSize : undefined,
      });
      setRooms(data);
    } catch (error) {
      console.error('Fehler beim Laden der Räume:', error);
      toast.error('Räume konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const tags = await getRoomTags();
        setAvailableTags(tags);
      } catch (e) {
        // ignore
      }
      if (selectedStandortId) {
        fetchRoomsData();
      } else {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStandortId]);

  useEffect(() => {
    if (selectedStandortId) {
      fetchRoomsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagIds.join(','), minSize, maxSize]);

  // Filter Räume basierend auf Suchbegriff und Filtern
  const filteredRooms = rooms.filter(room => {
    const lifecycleStatus = (room.status || (room.aktiv ? 'active' : 'inactive')) as 'draft' | 'active' | 'inactive';
    // Suchfilter
    const matchesSearch = searchTerm === '' || 
      room.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Tag-Filter
    const matchesTags = selectedTagIds.length === 0 || (room.tag_ids || []).some(id => selectedTagIds.includes(id));
    // Größenfilter
    const matchesMin = minSize === '' || (room.size_m2 ?? 0) >= (minSize as number);
    const matchesMax = maxSize === '' || (room.size_m2 ?? 0) <= (maxSize as number);
    // Status-Filter (draft/active/inactive)
    const matchesStatus = statusFilter === 'all' ? true : lifecycleStatus === statusFilter;
    return matchesSearch && matchesTags && matchesMin && matchesMax && matchesStatus;
  });

  // Navigiere zur vorherigen Woche
  const previousWeek = () => {
    setCurrentWeek(prevWeek => subWeeks(prevWeek, 1));
  };
  // Navigiere zur nächsten Woche
  const nextWeek = () => {
    setCurrentWeek(prevWeek => addWeeks(prevWeek, 1));
  };
  // Zurück zur aktuellen Woche
  const goToCurrentWeek = () => {
    setCurrentWeek(startOfWeek(heute(), {
      weekStartsOn: 1
    }));
  };
  // Prüfe, ob eine Buchung an einem bestimmten Tag für einen bestimmten Raum existiert
  const getRoomBookingsForDay = (roomId: number, date: Date) => {
    return bookings.filter(booking => booking.roomId === roomId && isSameDay(parseISO(booking.date), date));
  };
  // Helfer-Funktion für Raum-Status-Badge
  const getStatusBadgeClass = (status?: string) => {
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'draft':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getStatusText = (status?: string) => {
    if (!status) return 'Unbekannt';
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'inactive':
        return 'Inaktiv';
      case 'draft':
        return 'Entwurf';
      default:
        return status;
    }
  };
  const getRoomTypeText = (type?: string) => {
    if (!type) return 'Allgemein';
    switch (type) {
      // Bis hierher Platzhalter wie „Versammlungshalle“ und „Holonetz-Studio“ (T-0091).
      case 'practice':
        return 'Übungsraum';
      case 'classroom':
        return 'Unterrichtsraum';
      case 'concert':
        return 'Konzertsaal';
      case 'recording':
        return 'Tonstudio';
      default:
        return type;
    }
  };
  const getRoomTypeIcon = (type?: string) => {
    if (!type) return <LayoutGrid className="h-5 w-5" />;
    switch (type) {
      case 'practice':
        return <Music className="h-5 w-5 text-indigo-500" />;
      case 'classroom':
        return <Users className="h-5 w-5 text-emerald-500" />;
      case 'concert':
        return <Maximize className="h-5 w-5 text-purple-500" />;
      case 'recording':
        return <Headphones className="h-5 w-5 text-rose-500" />;
      default:
        return <LayoutGrid className="h-5 w-5" />;
    }
  };
  // Handle context menu opening
  const handleContextMenu = (e: React.MouseEvent, type: 'booking' | 'empty', data: any, room?: Room, day?: Date, time?: string) => {
    e.preventDefault();
    // Calculate position relative to viewport
    const rect = calendarRef.current?.getBoundingClientRect() || {
      left: 0,
      top: 0
    };
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setContextMenu({
      isOpen: true,
      position: {
        x,
        y
      },
      type,
      data,
      room,
      day,
      time
    });
  };
  // Close context menu
  const closeContextMenu = () => {
    setContextMenu(prev => ({
      ...prev,
      isOpen: false
    }));
  };
  // Handle menu item clicks
  const handleMenuItemClick = (action: string) => {
    const {
      data
    } = contextMenu;
    switch (action) {
      case 'viewPerson': {
        // Navigate to person search with name
        const personName = data.studentName || data.teacherName;
        if (personName) {
          globalThis.location.href = `/dashboard/search?q=${encodeURIComponent(personName)}`;
        }
        break;
      }
      case 'book': {
        if (!contextMenu.room || !contextMenu.day) break;
        const start = contextMenu.time || '09:00';
        const [h, m] = start.split(':').map(Number);
        setBuchung({
          open: true,
          roomId: contextMenu.room.id,
          roomName: contextMenu.room.name,
          date: format(contextMenu.day, 'yyyy-MM-dd'),
          start,
          end: `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`,
          title: '',
        });
        break;
      }
      case 'editBooking': {
        if (!data?.belegungId) break;
        setBuchung({
          open: true,
          belegungId: data.belegungId,
          roomId: data.roomId,
          roomName: contextMenu.room?.name || '',
          date: data.date,
          start: data.startTime,
          end: data.endTime,
          title: data.title,
        });
        break;
      }
      case 'deleteBooking': {
        if (!data?.belegungId) break;
        if (!globalThis.confirm(`Buchung „${data.title}“ löschen?`)) break;
        void raumbelegungenApi
          .delete(data.belegungId)
          .then(() => {
            toast.success('Buchung gelöscht');
            refetchBookings();
          })
          .catch(() => toast.error('Die Buchung konnte nicht gelöscht werden.'));
        break;
      }
    }
    closeContextMenu();
  };

  const saveBuchung = async () => {
    if (!buchung.title.trim() || !buchung.date || buchung.end <= buchung.start) {
      toast.error('Bitte Titel und eine gültige Zeit angeben.');
      return;
    }
    const payload = {
      raum_id: buchung.roomId,
      title: buchung.title.trim(),
      starts_at: `${buchung.date}T${buchung.start}:00`,
      ends_at: `${buchung.date}T${buchung.end}:00`,
    };
    setBuchungSaving(true);
    try {
      if (buchung.belegungId) await raumbelegungenApi.update(buchung.belegungId, payload);
      else await raumbelegungenApi.create(payload);
      toast.success(buchung.belegungId ? 'Buchung gespeichert' : 'Raum gebucht');
      setBuchung((b) => ({ ...b, open: false }));
      refetchBookings();
    } catch {
      toast.error('Der Raum ist zu dieser Zeit schon belegt – durch Unterricht oder eine andere Buchung.');
    } finally {
      setBuchungSaving(false);
    }
  };
  // Render context menu
  const renderContextMenu = () => {
    if (!contextMenu.isOpen) return null;
    const {
      position,
      type
    } = contextMenu;
    return <div className="absolute z-50 min-w-[160px] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95" style={{
      left: `${position.x}px`,
      top: `${position.y}px`
    }}>
        {type === 'empty' && canBook ? <div className="py-1">
            <button className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground" onClick={() => handleMenuItemClick('book')}>
              <PlusCircle className="mr-2 h-4 w-4" />
              <span>Raum buchen</span>
            </button>
          </div> : null}
        {type === 'booking' && contextMenu.data?.belegungId ? <div className="py-1">
            {canBook && <button className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground" onClick={() => handleMenuItemClick('editBooking')}>
                <Edit className="mr-2 h-4 w-4" />
                <span>Buchung bearbeiten</span>
              </button>}
            {canBook && <button className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground" onClick={() => handleMenuItemClick('deleteBooking')}>
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Buchung löschen</span>
              </button>}
          </div> : null}
        {type === 'booking' && !contextMenu.data?.belegungId ? <div className="py-1">
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              Terminänderungen erfolgen im Stundenplan.
            </p>
            {(contextMenu.data.studentName || contextMenu.data.teacherName) && <button className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground" onClick={() => handleMenuItemClick('viewPerson')}>
                <User className="mr-2 h-4 w-4" />
                <span>Zur Person springen</span>
              </button>}
          </div> : null}
      </div>;
  };
  // Render Grid View
  const renderGridView = () => {
    return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filteredRooms.map(room => <Card key={room.id} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                {/* Einen Raumtyp gibt es im Datenmodell nicht — ohne ihn stand überall
                    „Allgemein“ (T-0186). Angezeigt wird er nur, wenn er gesetzt ist. */}
                <div className="flex items-center">
                  {room.type ? (
                    <>
                      {getRoomTypeIcon(room.type)}
                      <span className="ml-2 text-sm text-muted-foreground">
                        {getRoomTypeText(room.type)}
                      </span>
                    </>
                  ) : null}
                </div>
                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(room.status || (room.aktiv ? 'active' : 'inactive'))}`}>
                  {getStatusText(room.status || (room.aktiv ? 'active' : 'inactive'))}
                </span>
              </div>
              <CardTitle className="mt-2">{room.name}</CardTitle>
              <CardDescription>{room.address || room.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Kapazität:</span>
                  <span>{room.kapazitaet || 0} Personen</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Größe:</span>
                  <span>{room.size_m2 ?? '-'} m²</span>
                </div>
                {room.tag_ids && room.tag_ids.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      Ausstattung:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {room.tag_ids.map((item, index) => {
                        const tag = availableTags.find(t => t.id === item);
                        return tag ? (
                          <div key={index} className="flex items-center rounded-full border bg-background px-2 py-1 text-xs">
                            <span className="ml-1">{tag.name}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="border-t bg-muted/50 px-6 py-3">
              <div className="flex justify-between w-full">
                <Button variant="outline" size="sm" onClick={() => setSelectedRoom(room)}>
                  Details
                </Button>
                {darfVerwalten ? (
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/standort/rooms">Verwalten</Link>
                  </Button>
                ) : null}
              </div>
            </CardFooter>
          </Card>)}
      </div>;
  };
  // Render Calendar View
  const renderCalendarView = () => {
    return <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={previousWeek}>
              <ChevronDown className="h-4 w-4 rotate-90" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
              Heute
            </Button>
            <Button variant="outline" size="sm" onClick={nextWeek}>
              <ChevronDown className="h-4 w-4 -rotate-90" />
            </Button>
          </div>
          <div className="text-sm font-medium">
            {format(weekDays[0], 'd. MMMM yyyy', {
            locale: de
          })}{' '}
            -{' '}
            {format(weekDays[6], 'd. MMMM yyyy', {
            locale: de
          })}
          </div>
        </div>
        <div className="rounded-lg border relative" ref={calendarRef} onClick={closeContextMenu}>
          {/* Calendar header */}
          <div className="grid grid-cols-8 border-b">
            <div className="p-3 text-center text-sm font-medium text-muted-foreground"></div>
            {weekDays.map((day, index) => <div key={index} className="p-3 text-center text-sm font-medium">
                <div>
                  {format(day, 'EEE', {
                locale: de
              })}
                </div>
                <div className={`text-lg ${isSameDay(day, heute()) ? 'rounded-full bg-primary text-primary-foreground h-8 w-8 flex items-center justify-center mx-auto' : ''}`}>
                  {format(day, 'd')}
                </div>
              </div>)}
          </div>
          {/* Calendar body */}
          <div>
            {filteredRooms.map(room => <div key={room.id} className="grid grid-cols-8 border-b last:border-b-0">
                {/* Room name */}
                <div className="border-r p-3 flex items-center">
                  <div>
                    <div className="font-medium">{room.name}</div>
                    {room.type ? (
                      <div className="flex items-center text-xs text-muted-foreground">
                        {getRoomTypeIcon(room.type)}
                        <span className="ml-1">{getRoomTypeText(room.type)}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
                {/* Days */}
                {weekDays.map((day, dayIndex) => {
              const dayBookings = getRoomBookingsForDay(room.id, day);
              return <div key={dayIndex} className="border-r last:border-r-0 p-1 relative" style={{
                height: timeSlots.length * 10 + 'px'
              }} onContextMenu={e => {
                // For empty cells, pass the first time slot as default
                if (dayBookings.length === 0) {
                  handleContextMenu(e, 'empty', null, room, day, '09:00');
                }
              }}>
                      {dayBookings.length === 0 ? <div className="h-full w-full rounded-md border border-dashed flex items-center justify-center" onContextMenu={e => handleContextMenu(e, 'empty', null, room, day, '09:00')}>
                          <span className="text-xs text-muted-foreground">
                            Frei
                          </span>
                        </div> : dayBookings.map(booking => {
                  // Calculate position based on time
                  const startHour = parseInt(booking.startTime.split(':')[0]);
                  const startMinute = parseInt(booking.startTime.split(':')[1]);
                  const endHour = parseInt(booking.endTime.split(':')[0]);
                  const endMinute = parseInt(booking.endTime.split(':')[1]);
                  // Oberkante = Beginn des Rasters aus den Einstellungen
                  const startTimeInMinutes = startHour * 60 + startMinute - rasterStartMinuten;
                  const endTimeInMinutes = endHour * 60 + endMinute - rasterStartMinuten;
                  const durationInMinutes = endTimeInMinutes - startTimeInMinutes;
                  // Jede Rasterzeile ist 10px hoch
                  const top = startTimeInMinutes / rasterIntervall * 10;
                  const height = durationInMinutes / rasterIntervall * 10;
                  return <div key={booking.id} className={`absolute inset-x-1 rounded-md border p-1 text-xs ${booking.color}`} style={{
                    top: `${top}px`,
                    height: `${height}px`,
                    maxHeight: `${timeSlots.length * 10}px`
                  }} onContextMenu={e => handleContextMenu(e, 'booking', booking, room, day)}>
                              <div className="font-medium truncate">
                                {booking.title}
                              </div>
                              {booking.teacherName && <div className="truncate">
                                  {booking.teacherName}
                                </div>}
                              <div className="flex items-center mt-1">
                                <Calendar className="h-3 w-3 mr-1" />
                                <span>
                                  {booking.startTime} - {booking.endTime}
                                </span>
                              </div>
                            </div>;})}
                    </div>;
            })}
              </div>)}
          </div>
          {/* Context Menu */}
          {renderContextMenu()}
      <Dialog open={buchung.open} onOpenChange={(open) => setBuchung((b) => ({ ...b, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{buchung.belegungId ? 'Buchung bearbeiten' : 'Raum buchen'}</DialogTitle>
            <DialogDescription>
              {buchung.roomName ? `${buchung.roomName} · ` : ''}Freie Buchung außerhalb des Unterrichts.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1">
              <Label htmlFor="buchung-titel">Titel</Label>
              <Input id="buchung-titel" value={buchung.title} placeholder="z. B. Elternabend" onChange={(e) => setBuchung((b) => ({ ...b, title: e.target.value }))} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label htmlFor="buchung-datum">Datum</Label>
                <Input id="buchung-datum" type="date" value={buchung.date} onChange={(e) => setBuchung((b) => ({ ...b, date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="buchung-von">Von</Label>
                <Input id="buchung-von" type="time" value={buchung.start} onChange={(e) => setBuchung((b) => ({ ...b, start: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="buchung-bis">Bis</Label>
                <Input id="buchung-bis" type="time" value={buchung.end} onChange={(e) => setBuchung((b) => ({ ...b, end: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuchung((b) => ({ ...b, open: false }))}>Abbrechen</Button>
            <Button onClick={() => void saveBuchung()} disabled={buchungSaving}>
              {buchung.belegungId ? 'Speichern' : 'Buchen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
      </div>;
  };
  // Render Room Detail Modal
  const renderRoomDetail = () => {
    if (!selectedRoom) return null;
    // Get upcoming bookings for this room
    const upcomingBookings = bookings.filter(booking => booking.roomId === selectedRoom.id).sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.startTime}`);
      const dateB = new Date(`${b.date}T${b.startTime}`);
      return dateA.getTime() - dateB.getTime();
    });
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-3xl rounded-lg bg-background p-6 shadow-lg">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getRoomTypeIcon(selectedRoom.type)}
              <h2 className="text-xl font-bold">{selectedRoom.name}</h2>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(selectedRoom.status)}`}>
                {getStatusText(selectedRoom.status)}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedRoom(null)} className="h-8 w-8 p-0">
              ✕
            </Button>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="mb-2 font-semibold">Details</h3>
              <div className="space-y-2 rounded-lg border p-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Adresse:</span>
                  <span>{selectedRoom.address || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Wegbeschreibung:</span>
                  <span>{selectedRoom.directions || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Größe:</span>
                  <span>{selectedRoom.size_m2 ?? '-'} m²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Kosten:</span>
                  <span>{selectedRoom.cost_cents != null ? `${(selectedRoom.cost_cents / 100).toFixed(2)} EUR` : '-'}</span>
                </div>
              </div>
              {selectedRoom.tag_ids && selectedRoom.tag_ids.length > 0 && (
                <>
                  <h3 className="mb-2 mt-4 font-semibold">Ausstattung</h3>
                  <div className="flex flex-wrap gap-2 rounded-lg border p-3">
                    {(selectedRoom as any).tags?.map((item: string, index: number) => <div key={index} className="flex items-center rounded-full border bg-background px-2 py-1 text-xs">
                        <span className="ml-1">{item}</span>
                      </div>)}
                  </div>
                </>
              )}
            </div>
            <div>
              <h3 className="mb-2 font-semibold">Bevorstehende Belegungen</h3>
              <div className="rounded-lg border">
                {upcomingBookings.length === 0 ? <div className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      Keine bevorstehenden Belegungen.
                    </p>
                  </div> : <div className="divide-y">
                    {upcomingBookings.slice(0, 5).map(booking => <div key={booking.id} className="p-3">
                        <div className="font-medium">{booking.title}</div>
                        <div className="mt-1 flex items-center text-sm">
                          <Calendar className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                          <span>
                            {format(parseISO(booking.date), 'dd.MM.yyyy')} •{' '}
                            {booking.startTime} - {booking.endTime}
                          </span>
                        </div>
                        {booking.teacherName && <div className="mt-1 text-sm">
                            Lehrer: {booking.teacherName}
                          </div>}
                        {booking.studentName && <div className="mt-1 text-sm">
                            Schüler: {booking.studentName}
                          </div>}
                      </div>)}
                  </div>}
              </div>
              {darfVerwalten ? (
                <div className="mt-4 flex space-x-2">
                  <Button className="flex-1" asChild>
                    <Link to="/standort/rooms">Raum verwalten</Link>
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Raumplan</h1>
      </div>
      {/* Filter und Suchleiste */}
      <div className="flex flex-col space-y-3">
        <Tabs defaultValue="grid" value={activeView} onValueChange={setActiveView} className="w-full">
          <TabsList>
            <TabsTrigger value="grid">Raumübersicht</TabsTrigger>
            <TabsTrigger value="calendar">Belegungsplan</TabsTrigger>
            <TabsTrigger value="day">Tagesansicht</TabsTrigger>
          </TabsList>
        </Tabs>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base">Filter</CardTitle>
            <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setFiltersOpen(v => !v)}>
              <ChevronDown className={`h-4 w-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className={`${filtersOpen ? 'flex' : 'hidden'} md:flex flex-wrap items-center gap-2`}>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input type="search" placeholder="Suchen..." className="pl-8 md:w-[200px] lg:w-[250px]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" placeholder="min m²" className="w-[100px]" value={minSize} onChange={e => setMinSize(e.target.value === '' ? '' : parseInt(e.target.value))} />
                <Input type="number" placeholder="max m²" className="w-[100px]" value={maxSize} onChange={e => setMaxSize(e.target.value === '' ? '' : parseInt(e.target.value))} />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="min-w-[120px]">
                    Tags{selectedTagIds.length > 0 ? ` (${selectedTagIds.length})` : ''}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 max-h-72 overflow-auto p-2">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Ausstattung wählen</span>
                    <button className="text-xs underline" onClick={() => setSelectedTagIds([])}>Zurücksetzen</button>
                  </div>
                  {availableTags.map(t => (
                    <label key={t.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTagIds.includes(t.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedTagIds(prev => checked ? [...prev, t.id] : prev.filter(id => id !== t.id));
                        }}
                      />
                      <span className="text-sm">{t.name}</span>
                    </label>
                  ))}
                  {availableTags.length === 0 && <div className="px-2 py-1 text-sm text-muted-foreground">Keine Tags vorhanden</div>}
                </DropdownMenuContent>
              </DropdownMenu>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <div className="flex items-center">
                    <Filter className="mr-2 h-4 w-4" />
                    <span>Status</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="inactive">Inaktiv</SelectItem>
                  <SelectItem value="draft">Entwürfe</SelectItem>
                </SelectContent>
              </Select>
              {darfVerwalten ? (
                <Button variant="outline" asChild>
                  <Link to="/standort/rooms">
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Räume verwalten
                  </Link>
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Räume-Ansicht */}
      {isLoading ? <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div> : filteredRooms.length === 0 ? <div className="rounded-lg border bg-card p-8 text-center">
          <LayoutGrid className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">Keine Räume gefunden</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Passe die Suchkriterien an oder lege unter „Räume &amp; Tags“ neue Räume an.
          </p>
          {darfVerwalten ? (
            <Button className="mt-4" asChild>
              <Link to="/standort/rooms">
                <PlusCircle className="mr-2 h-4 w-4" />
                Räume verwalten
              </Link>
            </Button>
          ) : null}
        </div> : <div>
          {activeView === 'grid' ? renderGridView() : activeView === 'calendar' ? renderCalendarView() : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedDate(d => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1))}><ChevronDown className="h-4 w-4 rotate-90" /></Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedDate(heute())}>Heute</Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedDate(d => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1))}><ChevronDown className="h-4 w-4 -rotate-90" /></Button>
                <div className="ml-4 text-sm font-medium">{format(selectedDate, 'EEEE, dd.MM.yyyy', { locale: de })}</div>
              </div>
              <div className="rounded-lg border overflow-auto">
                <div className="grid" style={{ gridTemplateColumns: `100px repeat(${filteredRooms.length}, minmax(180px, 1fr))` }}>
                  {/* Kopfzeile */}
                  <div className="border-b p-2 text-sm font-medium text-muted-foreground">Zeit</div>
                  {filteredRooms.map(r => <div key={r.id} className="border-b p-2 text-sm font-medium">{r.name}</div>)}
                  {/* Zeilen je Zeitslot */}
                  {generateTimeSlots().map((slot, idx) => (
                    <>
                      <div key={`time-${idx}`} className={`border-r p-2 text-xs ${slot.isHourStart ? 'bg-muted/40 font-medium' : ''}`}>{slot.label}</div>
                      {filteredRooms.map(r => {
                        const dayBookings = bookings.filter(b => b.roomId === r.id && b.date === format(selectedDate, 'yyyy-MM-dd'));
                        const bookingsAt = dayBookings.filter(b => b.startTime === slot.time);
                        return (
                          <div key={`cell-${r.id}-${idx}`} className="border-r p-1 h-10 relative">
                            {bookingsAt.map(b => (
                              <div key={b.id} className={`rounded border px-1 text-[10px] truncate ${b.color}`}>{b.title}</div>
                            ))}
                          </div>
                        );
                      })}
                    </>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>}
      {/* Room Detail Modal */}
      {selectedRoom && renderRoomDetail()}
    </div>;
};
export default RoomManagement;
